import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { TreePine, Mail, Globe } from 'lucide-react';
import { AUTH_ENABLED } from '../../lib/featureFlags';

const Footer = () => {
  const { t, language } = useLanguage();

  const footerText = {
    sv: {
      description: "Plattform för bättre beslut om värde, åtgärder och ekonomi i din skog",
      legal: "Juridisk information",
      impressum: "Impressum",
      privacy: "Integritetspolicy",
      terms: "Användarvillkor",
      sources: "Datakällor",
      sourcesText: "Alla beräkningar bygger på verifierade data från Sveriges ledande skogsinstitutioner",
      sourcesExplainer: "Vi använder samma statistik som professionella skogsvärderare",
      copyright: "Skogskalkyl utvecklas av",
      hodema: "Hodema edertools AB",
      tools: "Verktyg",
      calculators: "Kalkylatorer",
      myForest: "Min skog",
      savedCalc: "Sparade beräkningar",
      dataSources: "Datakällor"
    },
    de: {
      description: "Plattform für bessere Entscheidungen über Wert, Maßnahmen und Wirtschaftlichkeit Ihres Waldes",
      legal: "Rechtliches",
      impressum: "Impressum",
      privacy: "Datenschutzerklärung",
      terms: "Nutzungsbedingungen",
      sources: "Datenquellen",
      sourcesText: "Alle Berechnungen basieren auf verifizierten Daten führender schwedischer Forstinstitutionen",
      sourcesExplainer: "Wir nutzen dieselben Statistiken wie professionelle Forstgutachter",
      copyright: "Skogskalkyl wird entwickelt von",
      hodema: "Hodema edertools AB",
      tools: "Werkzeuge",
      calculators: "Rechner",
      myForest: "Mein Wald",
      savedCalc: "Gespeicherte Berechnungen",
      dataSources: "Datenquellen"
    },
    en: {
      description: "Platform for better decisions about value, actions and economics in your forest",
      legal: "Legal",
      impressum: "Legal Notice",
      privacy: "Privacy Policy",
      terms: "Terms of Use",
      sources: "Data Sources",
      sourcesText: "All calculations are based on verified data from Sweden's leading forestry institutions",
      sourcesExplainer: "We use the same statistics as professional forest valuers",
      copyright: "Skogskalkyl is developed by",
      hodema: "Hodema edertools AB",
      tools: "Tools",
      calculators: "Calculators",
      myForest: "My Forest",
      savedCalc: "Saved Calculations",
      dataSources: "Data Sources"
    },
    se: {
      description: "Plattform for buoret mearrádusat árvvu, doaimmaid ja ekonomiija birra du meahccis",
      legal: "Juridihkka",
      impressum: "Impressum",
      privacy: "Priváhtavuođapolitihkka",
      terms: "Geavahaneavttut",
      sources: "Diehtogáldut",
      sourcesText: "Buot rehkenastimat vuođđuduvvot duođaštuvvon dieđuide Ruoŧa meahcceinstitušuvnnain",
      sourcesExplainer: "Mii geavahit seamma statistihka go ámmátlaš meahcceárvvoštalli",
      copyright: "Skogskalkyl ovddiduvvo",
      hodema: "Hodema edertools AB bokte",
      tools: "Reaiddut",
      calculators: "Rehkenastit",
      myForest: "Mu meahcci",
      savedCalc: "Vurkkejuvvon rehkenastimat"
    }
  };

  const ft = footerText[language] || footerText.sv;

  return (
    <footer className="border-t bg-forest-900 text-forest-100 mt-12">
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Brand */}
          <div className="md:col-span-2">
            <div className="flex items-center gap-2 mb-4">
              <TreePine className="h-6 w-6 text-forest-400" />
              <span className="font-heading font-bold text-xl">Skogskalkyl</span>
            </div>
            <p className="text-forest-300 mb-4">{ft.description}</p>
            <div className="flex items-center gap-4 text-sm text-forest-400">
              <a href="mailto:info@hodema.se" className="flex items-center gap-1 hover:text-forest-200">
                <Mail className="h-4 w-4" />
                info@hodema.se
              </a>
            </div>
          </div>

          {/* Tools Links */}
          <div>
            <h4 className="font-semibold mb-4">{ft.tools}</h4>
            <ul className="space-y-2 text-forest-300">
              <li>
                <Link to="/verktyg" className="hover:text-forest-100 transition-colors">
                  {ft.calculators}
                </Link>
              </li>
              {AUTH_ENABLED && (
                <>
                  <li>
                    <Link to="/min-skog" className="hover:text-forest-100 transition-colors">
                      {ft.myForest}
                    </Link>
                  </li>
                  <li>
                    <Link to="/sparade-berakningar" className="hover:text-forest-100 transition-colors">
                      {ft.savedCalc}
                    </Link>
                  </li>
                </>
              )}
              <li>
                <Link to="/datakallor" className="hover:text-forest-100 transition-colors">
                  {ft.dataSources || ft.sources}
                </Link>
              </li>
            </ul>
          </div>

          {/* Legal Links */}
          <div>
            <h4 className="font-semibold mb-4">{ft.legal}</h4>
            <ul className="space-y-2 text-forest-300">
              <li>
                <Link to="/impressum" className="hover:text-forest-100 transition-colors">
                  {ft.impressum}
                </Link>
              </li>
              <li>
                <Link to="/datenschutz" className="hover:text-forest-100 transition-colors">
                  {ft.privacy}
                </Link>
              </li>
              <li>
                <Link to="/villkor" className="hover:text-forest-100 transition-colors">
                  {ft.terms}
                </Link>
              </li>
            </ul>
          </div>

          {/* Data Sources */}
          <div>
            <h4 className="font-semibold mb-4">{ft.sources}</h4>
            <p className="text-sm text-forest-300 mb-2">{ft.sourcesText}</p>
            <p className="text-xs text-forest-400 italic mb-3">{ft.sourcesExplainer}</p>
            <div className="flex flex-wrap gap-2">
              <a href="https://www.slu.se/riksskogstaxeringen" target="_blank" rel="noopener noreferrer" className="px-2 py-1 bg-forest-800 hover:bg-forest-700 rounded text-xs transition-colors">SLU Riksskogstaxeringen</a>
              <a href="https://www.skogsstyrelsen.se" target="_blank" rel="noopener noreferrer" className="px-2 py-1 bg-forest-800 hover:bg-forest-700 rounded text-xs transition-colors">Skogsstyrelsen</a>
              <a href="https://www.scb.se" target="_blank" rel="noopener noreferrer" className="px-2 py-1 bg-forest-800 hover:bg-forest-700 rounded text-xs transition-colors">SCB</a>
            </div>
          </div>
        </div>

        <div className="border-t border-forest-800 mt-8 pt-8 text-center text-sm text-forest-400">
          <p>
            {ft.copyright}{' '}
            <a 
              href="https://hodema.se" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-forest-300 hover:text-white transition-colors underline"
              data-testid="hodema-footer-link"
            >
              {ft.hodema}
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
